// Consumer complaints analysis (migration 044; Amit 31 Oct 2026): the last 7 days, and rolling 13, 26
// and 52 weeks, each against the same weeks a year earlier (PY), with the consumers' photos inline.
// Everyone signed in since 2 Nov 2026 (QA team feedback: "so everyone can see"); the consumers' photos and
// the case numbers stay with complaint readers (caseDetail). CPMU mirrors the CONSUMERv2 report: every
// complaint over the model's invoiced units, target 8. Cases from 1 Jul 2026 carry the SOP clocks; older AC
// deals are history rows, read once, for the comparison only. A PY window the history does not reach
// says so: a missing year is never shown as a fall.
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/Breadcrumb';
import { officeFocus, useResource } from '../../shells/OfficeShell';
import { complaintPhotoPath } from './ComplaintPhotos';
import { PhotoImage } from './LinePhotos';
import { SEVERITY_TONE, day, hoursText, type Severity } from './complaintsModel';

type Win = '7d' | '13w' | '26w' | '52w';
const WINS: [Win, string][] = [
  ['7d', 'Last 7 days'],
  ['13w', '13 weeks'],
  ['26w', '26 weeks'],
  ['52w', '52 weeks'],
];

interface Breakdown {
  key: string;
  current: number;
  py: number;
}
interface Analysis {
  label: string;
  from: string;
  to: string;
  pyFrom: string;
  pyTo: string;
  pyMissingDays: number | null;
  currentMissingDays: number | null;
  importing: { left: number; from: string; to: string } | null;
  historyFrom: string | null;
  totals: { current: number; py: number; changePct: number | null; high: number; pyHigh: number; previous: number | null };
  service: { cases: number; ackWithinDayPct: number | null; medianAckHours: number | null; medianCloseDays: number | null; openOverClock: number };
  trend: Array<{ label: string; start: string; current: number; py: number; high: number }>;
  bySeverity: Breakdown[];
  byCategory: Breakdown[];
  byProduct: Breakdown[];
  byRetailer: Breakdown[];
  clusters: Array<{ batch: string; product: string; count: number; caseIds: number[]; high: number }>;
  photos: Array<{ caseId: number; photoId: number; count: number; receivedAt: string; product: string | null; category: string | null; severity: string | null }>;
  /** false: the reader holds no complaint grant, so photos and case numbers are left out. Absent from an older API. */
  caseDetail?: boolean;
  /** Absent from an older API. */
  cpmu?: Cpmu;
}

interface CpmuFigure {
  cases: number;
  units: number | null;
  cpmu: number | null;
}
interface Cpmu {
  target: number;
  source: string;
  unitsUnknown: string | null;
  window: { current: CpmuFigure; py: CpmuFigure };
  /** The report's fiscal year (whole year, against the whole of last year); null when not read. */
  fiscalYear?: { label: string; pyLabel: string; from: string; to: string; current: CpmuFigure; py: CpmuFigure } | null;
  trend: Array<{ start: string; current: number | null; py: number | null }>;
}

const card = 'flex flex-col gap-(--s3) rounded-md border border-(--border) bg-(--card) p-(--s4)';
const h2 = 'm-0 text-(length:--fs-sm) font-semibold';
const lastDay = (iso: string) => day(new Date(new Date(iso).getTime() - 1).toISOString());
/** The window's last day, or today while the window is still running. */
const untilDay = (iso: string) => (new Date(iso).getTime() > Date.now() ? 'today' : lastDay(iso));
const caseNo = (id: number) => `CMP-${String(id).padStart(4, '0')}`;

function Change({ cur, py, missing }: { cur: number; py: number; missing: boolean }) {
  if (missing) return <span className="text-(--muted)">not compared yet</span>;
  if (!py) return <span className="text-(--muted)">none last year</span>;
  const pct = Math.round(((cur - py) / py) * 100);
  const tone = pct > 0 ? 'text-(--out-fg)' : pct < 0 ? 'text-(--in-fg)' : 'text-(--muted)';
  return <span className={`font-semibold tabular-nums ${tone}`}>{pct > 0 ? `+${pct}` : pct}% vs last year</span>;
}

const units = (n: number | null) => (n === null ? 'units not known' : `${(n / 1_000_000).toFixed(2)} M units`);
const cpmuText = (v: number | null) => (v === null ? 'not known' : v.toFixed(1));

/** CPMU as the CONSUMERv2 report shows it: complaints per million units invoiced, against the target. */
function CpmuPanel({ c, a }: { c: Cpmu; a: Analysis }) {
  const fy = c.fiscalYear ?? null;
  const tile = (label: string, f: CpmuFigure, py: CpmuFigure, pyLabel: string) => {
    const over = f.cpmu !== null && f.cpmu > c.target;
    return (
      <div className="rounded-md border border-(--border) bg-(--card) px-(--s4) py-(--s3)">
        <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted)">{label}</dt>
        <dd className={`m-0 font-display text-[30px] font-semibold leading-tight tabular-nums ${over ? 'text-(--out-fg)' : ''}`}>{cpmuText(f.cpmu)}</dd>
        <dd className="m-0 flex flex-wrap gap-x-(--s2) text-[12px] text-(--muted)">
          <span>
            {f.cases} complaints · {units(f.units)}
          </span>
          <span>
            {pyLabel} {cpmuText(py.cpmu)}
          </span>
          {f.cpmu !== null ? <span className={over ? 'font-semibold text-(--out-fg)' : 'font-semibold text-(--in-fg)'}>{over ? 'over' : 'within'} target {c.target}</span> : null}
        </dd>
      </div>
    );
  };
  const pts = c.trend.filter((t) => t.current !== null || t.py !== null);
  const W = 760;
  const H = 160;
  const pad = { l: 28, r: 8, t: 10, b: 24 };
  const n = c.trend.length;
  const max = Math.max(c.target * 1.5, ...c.trend.flatMap((t) => [t.current ?? 0, t.py ?? 0]));
  const bw = (W - pad.l - pad.r) / Math.max(1, n);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const path = (k: 'current' | 'py') =>
    c.trend
      .map((t, i) => (t[k] === null ? null : `${pad.l + bw * i + bw / 2},${y(t[k] as number)}`))
      .reduce<string>((acc, p, i, arr) => (p === null ? acc : `${acc}${acc && arr[i - 1] !== null ? 'L' : 'M'}${p}`), '');
  const showPy = a.pyMissingDays === null && a.currentMissingDays === null;
  return (
    <section className={card} aria-labelledby="cpmu-title">
      <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
        <h2 id="cpmu-title" className={h2}>
          CPMU: complaints per million units
        </h2>
        <span className="text-[12px] text-(--muted)">Every complaint over units invoiced, as the CONSUMERv2 report counts them. Target {c.target}.</span>
      </div>
      {c.unitsUnknown ? (
        <p role="status" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          Units invoiced could not be read from CONSUMERv2 ({c.unitsUnknown}), so CPMU is not known. This is not a CPMU of zero.
        </p>
      ) : null}
      <dl className="m-0 grid grid-cols-1 gap-(--s3) sm:grid-cols-2">
        {fy ? tile(fy.label, fy.current, fy.py, fy.pyLabel) : null}
        {tile(a.label, c.window.current, c.window.py, 'same weeks last year')}
      </dl>
      {n > 1 && pts.length ? (
        <figure className="m-0 flex flex-col gap-(--s1)">
          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full min-w-[560px]" aria-hidden="true">
              <line x1={pad.l} x2={W - pad.r} y1={y(c.target)} y2={y(c.target)} stroke="var(--out-fg)" strokeWidth="1" strokeDasharray="3 3" />
              <text x={pad.l - 6} y={y(c.target) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
                {c.target}
              </text>
              <path d={path('current')} fill="none" stroke="var(--accent-line)" strokeWidth="2.5" />
              {showPy ? <path d={path('py')} fill="none" stroke="var(--fg, currentColor)" strokeWidth="1.5" strokeDasharray="5 4" /> : null}
            </svg>
          </div>
          <figcaption className="text-[12px] text-(--muted)">
            CPMU per {n === 7 ? 'day' : 'week'}; dotted red line the target{showPy ? ', dashed line the same weeks last year' : ''}. A single week swings: one complaint in a quiet week reads high.
          </figcaption>
          <table className="sr-only">
            <caption>CPMU per {n === 7 ? 'day' : 'week'}</caption>
            <thead>
              <tr>
                <th scope="col">{n === 7 ? 'Day' : 'Week of'}</th>
                <th scope="col">CPMU</th>
                <th scope="col">Same weeks last year</th>
              </tr>
            </thead>
            <tbody>
              {c.trend.map((t) => (
                <tr key={t.start}>
                  <th scope="row">{day(t.start)}</th>
                  <td>{cpmuText(t.current)}</td>
                  <td>{cpmuText(t.py)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </figure>
      ) : null}
    </section>
  );
}

/** Bars for the window, PY as a line over them, the High share darker in each bar. */
function TrendChart({ a }: { a: Analysis }) {
  const W = 760;
  const H = 220;
  const pad = { l: 28, r: 8, t: 10, b: 34 };
  const n = a.trend.length;
  const max = Math.max(1, ...a.trend.map((t) => Math.max(t.current, t.py)));
  const bw = (W - pad.l - pad.r) / n;
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const ticks = [...new Set([0, Math.round(max / 2), max])];
  const every = n > 26 ? 4 : n > 13 ? 2 : 1;
  const line = a.trend.map((t, i) => `${i ? 'L' : 'M'}${pad.l + bw * i + bw / 2},${y(t.py)}`).join(' ');
  return (
    <figure className="m-0 flex flex-col gap-(--s2)">
      <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full min-w-[560px]" aria-hidden="true">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth="1" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
              {t}
            </text>
          </g>
        ))}
        {a.trend.map((t, i) => {
          const x = pad.l + bw * i + bw * 0.15;
          const w = bw * 0.7;
          return (
            <g key={t.start}>
              <title>{`${t.label}: ${t.current} (High ${t.high}), last year ${t.py}`}</title>
              <rect x={x} y={y(t.current)} width={w} height={y(0) - y(t.current)} fill="var(--n-400, #9aa3ad)" rx="2" />
              {t.high ? <rect x={x} y={y(t.high)} width={w} height={y(0) - y(t.high)} fill="var(--accent-line)" rx="2" /> : null}
              {i % every === 0 ? (
                <text x={pad.l + bw * i + bw / 2} y={H - pad.b + 16} textAnchor="middle" fontSize="11" fill="var(--muted)">
                  {t.label}
                </text>
              ) : null}
            </g>
          );
        })}
        {/* Prior year only when the history covers all of it: a missing year never draws as a fall. */}
        {a.pyMissingDays === null && a.currentMissingDays === null ? <path d={line} fill="none" stroke="var(--fg, currentColor)" strokeWidth="2" strokeDasharray="5 4" /> : null}
      </svg>
      </div>
      <table className="sr-only">
        <caption>Complaints per {n === 7 ? 'day' : 'week'}</caption>
        <thead>
          <tr>
            <th scope="col">{n === 7 ? 'Day' : 'Week of'}</th>
            <th scope="col">Complaints</th>
            <th scope="col">High</th>
            <th scope="col">Same weeks last year</th>
          </tr>
        </thead>
        <tbody>
          {a.trend.map((t) => (
            <tr key={t.start}>
              <th scope="row">{t.label}</th>
              <td>{t.current}</td>
              <td>{t.high}</td>
              <td>{a.pyMissingDays === null && a.currentMissingDays === null ? t.py : 'not covered'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <figcaption className="flex flex-wrap gap-(--s3) text-[12px] text-(--muted)">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-(--n-400,#9aa3ad)" /> this period
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-(--accent-line)" /> of which High
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 border-t-2 border-dashed border-current" /> same weeks last year
        </span>
      </figcaption>
    </figure>
  );
}

function BreakdownTable({ title, rows, pyMissing }: { title: string; rows: Breakdown[]; pyMissing: boolean }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.current, r.py)));
  return (
    <section className={card}>
      <h2 className={h2}>{title}</h2>
      {rows.length === 0 ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">None in this period.</p>
      ) : (
        <table className="w-full border-collapse text-(length:--fs-sm)">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-(--muted)">
              <th className="py-1 font-semibold">
                <span className="sr-only">{title.replace(/^By /, '')}</span>
              </th>
              <th className="w-[36%] py-1 font-semibold">
                <span className="sr-only">Share</span>
              </th>
              <th className="w-[3.5rem] py-1 pl-(--s3) text-right font-semibold">Now</th>
              <th className="w-[4.5rem] py-1 pl-(--s3) text-right font-semibold">Last year</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-(--border)">
                <td className="py-1.5 pr-(--s3) break-words">{r.key}</td>
                <td className="py-1.5">
                  <div className="relative h-2.5 rounded-sm bg-(--subtle)">
                    <div className="absolute inset-y-0 left-0 rounded-sm bg-(--n-400,#9aa3ad)" style={{ width: `${(r.current / max) * 100}%` }} />
                    {!pyMissing && r.py ? <div className="absolute inset-y-[-2px] w-[2px] bg-(--fg,currentColor)" style={{ left: `${(r.py / max) * 100}%` }} title={`last year ${r.py}`} /> : null}
                  </div>
                </td>
                <td className="py-1.5 pl-(--s3) text-right font-semibold tabular-nums">{r.current}</td>
                <td className="py-1.5 pl-(--s3) text-right tabular-nums text-(--muted)">{pyMissing ? '' : r.py}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function ComplaintAnalysis() {
  // The period and the filter live in the URL, so a view can be shared and survives a reload.
  const [params, setParams] = useSearchParams();
  const win: Win = (WINS.find(([k]) => k === params.get('window'))?.[0] as Win | undefined) ?? '13w';
  const faults = params.get('faults') === '1';
  const set = (w: Win, f: boolean) => setParams(f ? { window: w, faults: '1' } : { window: w }, { replace: true });
  const res = useResource<{ analysis: Analysis }>(`/complaints/analysis?window=${win}${faults ? '&faults=1' : ''}`);
  const a = res.data?.analysis;
  const label = WINS.find(([k]) => k === win)?.[1] ?? '';
  // A comparison is shown only when both periods are fully covered by the data.
  const pyMissing = Boolean(a?.pyMissingDays || a?.currentMissingDays);

  return (
    <div className="flex flex-col gap-(--s5)">
      <Breadcrumb items={[{ label: 'Complaints', to: '/office/complaints' }, { label: 'Analysis' }]} />
      <header className="flex flex-wrap items-end justify-between gap-(--s3)">
        <div>
          <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Complaints analysis</h1>
          <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            CPMU and trends against the same weeks last year, by severity, category, product and retailer, with the batches that repeat and the consumers' photos.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-(--s2)" role="group" aria-label="Period">
          {WINS.map(([k, label]) => (
            <Button key={k} type="button" variant={win === k ? 'default' : 'outline'} aria-pressed={win === k} onClick={() => set(k, faults)}>
              {label}
            </Button>
          ))}
          <label className="ml-(--s2) inline-flex min-h-[44px] cursor-pointer items-center gap-(--s2) text-(length:--fs-sm)">
            <input type="checkbox" checked={faults} onChange={(e) => set(win, e.target.checked)} className={`h-4 w-4 ${officeFocus}`} />
            Product faults only
          </label>
        </div>
      </header>

      {res.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read the analysis: {res.error}</p>
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      ) : !a ? (
        <p role="status" className="m-0 text-(--muted)">
          Reading {label.toLowerCase()}…
        </p>
      ) : (
        <div aria-busy={res.loading} className={`flex flex-col gap-(--s5) transition-opacity ${res.loading ? 'opacity-60' : ''}`}>
          {res.loading ? (
            <p role="status" className="sr-only">
              Reading {label.toLowerCase()}…
            </p>
          ) : null}
          <p className="m-0 text-(length:--fs-sm) text-(--muted)">
            {a.label}: {day(a.from)} to {untilDay(a.to)} · compared with {day(a.pyFrom)} to {lastDay(a.pyTo)}
          </p>
          {a.importing && (a.pyMissingDays || a.currentMissingDays) ? (
            <p role="status" className="m-0 rounded-md bg-(--action-bg) px-(--s3) py-(--s2) text-(length:--fs-sm) text-(--action-fg)">
              Older complaints are still being read from ActiveCampaign{a.importing.left > 0 ? ` (${a.importing.left} left)` : ''}: {day(a.importing.from)} to {day(a.importing.to)} is not in yet, so this period is not compared with last year until it is. About 60 are read every 10 minutes.
            </p>
          ) : a.pyMissingDays ? (
            <p role="status" className="m-0 text-(length:--fs-sm) text-(--muted)">
              The data starts {day(a.historyFrom)}, so the prior year is not complete ({a.pyMissingDays} days missing) and is not compared.
            </p>
          ) : null}

          {a.cpmu ? <CpmuPanel c={a.cpmu} a={a} /> : null}

          <dl className="m-0 grid grid-cols-2 gap-(--s3) sm:grid-cols-4">
            {[
              ['Complaints', a.totals.current, <Change key="c" cur={a.totals.current} py={a.totals.py} missing={pyMissing} />, [a.totals.previous !== null ? `previous 7 days ${a.totals.previous}` : null, pyMissing ? null : `last year ${a.totals.py}`].filter(Boolean).join(' · ')],
              ['High', a.totals.high, <Change key="h" cur={a.totals.high} py={a.totals.pyHigh} missing={pyMissing} />, pyMissing ? '' : `last year ${a.totals.pyHigh}`],
              ['Acknowledged in 1 day', a.service.ackWithinDayPct === null ? 'none' : `${a.service.ackWithinDayPct}%`, null, `${a.service.cases} cases since 1 Jul${a.service.medianAckHours !== null ? ` · median ${hoursText(Math.round(a.service.medianAckHours))}` : ''}`],
              ['Open over a clock', a.service.openOverClock, null, a.service.medianCloseDays !== null ? `median close ${Math.round(a.service.medianCloseDays)} days` : 'none closed in the period'],
            ].map(([label, n, change, sub]) => (
              <div key={label as string} className="rounded-md border border-(--border) bg-(--card) px-(--s4) py-(--s3)">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted)">{label}</dt>
                <dd className="m-0 font-display text-[30px] font-semibold leading-tight tabular-nums">{n}</dd>
                <dd className="m-0 flex flex-wrap gap-x-(--s2) text-[12px] text-(--muted)">
                  {change}
                  {sub ? <span>{sub}</span> : null}
                </dd>
              </div>
            ))}
          </dl>

          <section className={card}>
            <h2 className={h2}>Complaints per {a.trend.length === 7 ? 'day' : 'week'}</h2>
            <TrendChart a={a} />
          </section>

          <div className="grid gap-(--s4) lg:grid-cols-2">
            <BreakdownTable title="By severity" rows={a.bySeverity} pyMissing={pyMissing} />
            <BreakdownTable title="By retailer" rows={a.byRetailer} pyMissing={pyMissing} />
            <BreakdownTable title="By category (top 12)" rows={a.byCategory} pyMissing={pyMissing} />
            <BreakdownTable title="By product (top 12)" rows={a.byProduct} pyMissing={pyMissing} />
          </div>

          <section className={card}>
            <h2 className={h2}>Batches with more than one complaint</h2>
            {a.clusters.length === 0 ? (
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">No batch repeats in this period.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-(--s2) p-0 text-(length:--fs-sm)">
                {a.clusters.map((c) => (
                  <li key={`${c.product}|${c.batch}`} className="flex flex-wrap items-center gap-(--s2)">
                    <strong>
                      {c.product}, batch {c.batch}
                    </strong>
                    <span className="tabular-nums">{c.count} complaints</span>
                    {c.high ? <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_TONE.High}`}>{c.high} High</span> : null}
                    {c.caseIds.map((id) => (
                      <Link key={id} to={`/office/complaints/${id}`} className={`underline ${officeFocus}`}>
                        {caseNo(id)}
                      </Link>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={card}>
            <h2 className={h2}>
              Photos from consumers{a.caseDetail === false ? '' : `: ${a.photos.length} case${a.photos.length === 1 ? '' : 's'}`}
            </h2>
            {a.caseDetail === false ? (
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">
                The consumers' photos and case numbers are for people who work complaints (COMPLAINT_TRIAGE or RELEASE).
              </p>
            ) : a.photos.length === 0 ? (
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">No photos on cases in this period (history rows carry none).</p>
            ) : (
              <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-(--s3) p-0">
                {a.photos.map((p) => (
                  <li key={p.caseId}>
                    <Link to={`/office/complaints/${p.caseId}`} className={`flex flex-col gap-(--s1) rounded-(--radius-sm) ${officeFocus}`}>
                      <span className="relative block">
                        <PhotoImage path={complaintPhotoPath(p.caseId, p.photoId, true)} alt={`${caseNo(p.caseId)}: the consumer's photo`} size="thumb" className="aspect-square w-full" lazy interactive={false} />
                        {p.count > 1 ? (
                          <span className="absolute right-1.5 bottom-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-semibold text-white tabular-nums">{p.count} photos</span>
                        ) : null}
                      </span>
                      <span className="text-[12px]">
                        <strong>{caseNo(p.caseId)}</strong> · {day(p.receivedAt)}
                        {p.severity ? (
                          <span className={`ml-1 rounded-full px-1.5 text-[10px] font-semibold ${SEVERITY_TONE[p.severity as Severity] ?? ''}`}>{p.severity}</span>
                        ) : null}
                      </span>
                      <span className="text-[12px] text-(--muted)">{[p.product, p.category].filter(Boolean).join(' · ')}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
